/**
 * CreateDbLinkDialog - Form-based dialog for creating Oracle DB Links.
 *
 * Fields: Link Name, Connect String, Username, Password, Public toggle.
 * DDL preview panel shows the generated CREATE [PUBLIC] DATABASE LINK statement.
 */

import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useOracleObjectDialogStore } from '../store/oracleObjectDialogStore';
import { generateCreateDbLinkDDL } from '../utils/ddlGenerators';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

export function CreateDbLinkDialog() {
  const { openDialog, context, close } = useOracleObjectDialogStore();
  const isOpen = openDialog === 'create-dblink' && !!context;

  const [name, setName] = useState('');
  const [connectString, setConnectString] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const ddl = useMemo(() => {
    if (!context) return '';
    return generateCreateDbLinkDDL({
      name: name || 'NEW_DBLINK',
      connectString: connectString || 'host:port/service_name',
      username,
      password,
      isPublic,
    });
  }, [context, name, connectString, username, password, isPublic]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
      close();
    }
  };

  const resetForm = () => {
    setName('');
    setConnectString('');
    setUsername('');
    setPassword('');
    setIsPublic(false);
  };

  const handleCopyToEditor = () => {
    if (!context) return;
    openQueryTab(context.connectionId, context.database, ddl, 'New DB Link');
    resetForm();
    close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px]" data-testid="create-dblink-dialog">
        <DialogHeader>
          <DialogTitle>New Database Link</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Form Fields */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="dbl-name">Link Name</Label>
              <Input id="dbl-name" data-testid="dbl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="NEW_DBLINK" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="dbl-connect">Connect String (host:port/service)</Label>
              <Input id="dbl-connect" data-testid="dbl-connect" value={connectString} onChange={(e) => setConnectString(e.target.value)} placeholder="host:port/service_name" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="dbl-user">Username</Label>
              <Input id="dbl-user" data-testid="dbl-user" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="remote_user" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="dbl-pass">Password</Label>
              <Input id="dbl-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" className="mt-1" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch id="dbl-public" checked={isPublic} onCheckedChange={setIsPublic} />
              <Label htmlFor="dbl-public">Public DB Link</Label>
            </div>
          </div>

          {/* DDL Preview */}
          <div>
            <Label>DDL Preview</Label>
            <pre
              className="mt-1 p-3 bg-muted rounded-md text-xs font-mono whitespace-pre-wrap min-h-[200px] border border-border overflow-auto max-h-[300px]"
              data-testid="dbl-ddl-preview"
            >
              {ddl}
            </pre>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={handleCopyToEditor} data-testid="dbl-copy-to-editor">
            Copy to Query Editor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function openQueryTab(connectionId: string, database: string, ddl: string, title: string) {
  const { activeConnection, connection } = getConnectionById(connectionId);
  if (!activeConnection || !connection) return;

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${title}-${queryNumber}`,
    type: 'query',
    content: { query: ddl },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database,
    },
  });
}
